import { ChangeDetectionStrategy, Component, ViewChild, OnInit, OnDestroy, Input, EventEmitter, Output, Injector } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { Pagination, PermissionSetService, PermissionSetAssigneeTargetService, AssigneeAppletDtoModel, TenantPermissionAssigneeTargetLinkService } from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../../../../../../../../../../modules/permission/facades/permission.facade';
import { RoleModel, UserModel } from '../../../../../../../../../../../../models/user-permissions.model';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../../../../../../../../listing.utils';
import { PaginationComponent } from '../../../../../../../../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../../../../../../../../view-column.component';
import { AppConfig } from '../../../../../../../../../../../../visa';
import { forkJoin, iif, Observable, of, zip } from 'rxjs';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../../../../../../../../permission-controller/root';
import { UserPermSelectors } from '../../../../../../../../../../../../modules/permission/user-permissions-controller/selectors';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { UserPermActions } from '../../../../../../../../../../../../modules/permission/user-permissions-controller/actions';
import { SearchQueryModel } from '../../../../../../../../../../../../models/query.model';
import { RowHeightParams } from 'ag-grid-enterprise';
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: 'app-permission-definition',
    templateUrl: './permission-definition.component.html',
    styleUrls: ['./permission-definition.component.scss']
})

export class PermissionDefinitionComponent extends ViewColumnComponent implements OnInit, OnDestroy {

    @ViewChild(MatTabGroup, {static:true}) matTab: MatTabGroup;

    @ViewChild(PaginationComponent, {static: false})
    private paginationComponent: PaginationComponent;

    @Input() localState: any;

    // api visa
    apiVisa = AppConfig.apiVisa;

    // initial ag grid state
    gridApi;
    gridOptions = gridOptionsServerSide;
    columnsDefs;
    rowSelection = 'single';

    permSetLineGuid: string;

    // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
    private subSink = new SubSink;

    appletGuid = sessionStorage.getItem('appletGuid');

    //translation variable
    permission_code_text: string;
    permission_name_text: string;
    permission_target_text: string;
    permission_definition_text: string;

    constructor(
        // private fb: FormBuilder,
        private store: Store<PermissionStates>,
        private viewColFacade: PermissionFacade,
        // private readonly componentStore: ComponentStore<{localState: LocalState}>,
        private permissionSetService: PermissionSetService,
        private permissionSetAssigneeTargetService: PermissionSetAssigneeTargetService,
        private tenantPermissionAssigneeTargetLinkService: TenantPermissionAssigneeTargetLinkService,
        private injector: Injector
      ) {
        super();
        const translateService = this.injector.get(TranslateService, null);
        if (translateService) {
          translateService.get('labels').subscribe(res => {
            this.permission_code_text = res.permission_code;
            this.permission_name_text = res.permission_name;
            this.permission_target_text = res.permission_target;
            this.permission_definition_text = res.permission_definition;
          });
        } else {
          this.permission_code_text = 'Permission Code';
          this.permission_name_text = 'Permission Name';
          this.permission_target_text = 'Permission Target';
          this.permission_definition_text = 'Permission Definition';
        }
      }

    /**
     * Lifecycle method: Called when component is first initialised.
     */
    ngOnInit(): void {

        this.columnsDefs = [
            {
                headerName: this.permission_code_text,
                field: 'app_perm_dfn.perm_code',
                cellStyle: {textAlign: 'left'}
            },
            {
                headerName: this.permission_name_text,
                field: 'app_perm_dfn.perm_name',
                cellStyle: {textAlign: 'left'}
            },
            {
                headerName: this.permission_target_text,
                field: 'app_perm_dfn.target_options.target_hdr_table_view_name',
                cellStyle: {textAlign: 'left'}
            },
            {
                headerName: this.permission_definition_text,
                field: 'app_perm_dfn.perm_description',
                cellStyle: {textAlign: 'left'}
            }
        ]
    }

    /**
     * This function is called when AG grid is first initialised and initially
     * set the datasource of the grid.
     *
     * @param params : grid parameters
     */
    onGridReady(params) {
        this.gridApi = params.api;
        this.gridApi.closeToolPanel();

        this.subSink.sink = this.store.select(UserPermSelectors.selectSelectedPermSetLine).subscribe(permSetLine => {
            console.log("permSetLine",permSetLine);
            if(permSetLine) {
                this.permSetLineGuid = permSetLine.guid.toString();
                console.log("permSetLineGuid: ",this.permSetLineGuid);

                const datasource = this.getDatasource();
                this.gridApi.setServerSideDatasource(datasource);
            }
        })
    }

    /**
     * Returns the datasource for the AG grid.
     *
     * @param conditionalCriteria : any custom criteria to search for in the API
     * @returns the datasource
     */
    getDatasource(conditionalCriteria?){
        let offset = 0;
        let limit = this.paginationComponent.rowPerPage;

        return {
            getRows: grid => {

                const sortModel = grid.request.sortModel;
                const filterModel = grid.request.filterModel;
                const sortOn = pageSorting(sortModel);
                const filter = pageFiltering(filterModel);

                // only change offset and limit if no filterModel or sortModel is present
                if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
                    offset = grid.request.startRow;
                    limit = grid.request.endRow - offset;
                }

                let dto = {
                  "applet_guid": this.appletGuid,
                  "perm_set_line_guids": [this.permSetLineGuid]
                }

                // const pagination = new Pagination(offset, limit, conditionalCriteria);
                this.subSink.sink = this.tenantPermissionAssigneeTargetLinkService.getPermDefinition(dto, this.apiVisa)
                .subscribe(a => {
                    console.log("a", a);
                    console.log("a.data", a.data);
                    // put data into the grid
                    const data = sortOn(a.data).filter(entity => filter.by(entity));
                    const totalRecords = filter.isFiltering ? a.data.length : data.length;
                    // const totalRecords = filter.isFiltering ? a.totalRecords : data.length;

                    // force pagination to go to first page if filter is applied
                    if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }

                    grid.successCallback(data, totalRecords);
                    this.store.dispatch(UserPermActions.loadPermissionSetSuccess({totalRecords: a.data.length}));
                    }, err => {
                    grid.failCallback();
                    this.store.dispatch(UserPermActions.loadPermissionSetFailure({error: err.message}));
                })
            }
        }

    }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
